import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FaEdit, FaTrash } from "react-icons/fa";
import { Header } from "../../components/Header";
import { isAdmin } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import {
  deleteIntermediario,
  getClientes,
  getIntermediarios,
  getUsers,
  log,
  quitarCaracteresEspeciales,
  saveIntermediario,
  updateIntermediario,
} from "../../utils";
import {
  actualizarProveedor,
  getNombreProveedor,
  getProveedores,
  getRazonesSocialesIntermediarioFacturadas,
  normalizarNombreSugerencia,
} from "../../utils/proveedores";
import { useTableSort } from "../../utils/useTableSort";
import styles from "./stylesIntermediarios.module.scss";

// Fase 1 comisión de agencias: la gestión del vínculo razón social (proveedor) ↔ agencia
// (intermediario) es solo Chile por ahora. proveedores.pais usa 'Chile'/'Perú' (regla 10.53).
const PAIS_VINCULO_RAZONES = "Chile";

const EMPTY_FORM = {
  nombre: "",
  comision: "",
  comision_2: "",
  comision_2_fechainicio: "",
  comision_2_fechafin: "",
  es_acuerdo_volumen: false,
  holding_id: "",
};

export const CargarIntermediario = () => {
  const [intermediarios, setIntermediarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [searchNombre, setSearchNombre] = useState("");
  const [saving, setSaving] = useState(false);
  const [detailModal, setDetailModal] = useState(null); // { type: "clientes"|"usuarios", inter }
  const [proveedores, setProveedores] = useState([]);
  const [razonesSugeridas, setRazonesSugeridas] = useState(() => new Set());
  const [vinculoModal, setVinculoModal] = useState(null); // intermediario cuya ficha de razones sociales se edita
  const [searchProv, setSearchProv] = useState("");
  const [vinculando, setVinculando] = useState(false);

  const { pais, id: userId } = useAuthContext();
  const esAdmin = isAdmin(userId);
  // Vínculo razón social ↔ agencia: solo Chile por ahora (alcance Fase 1).
  const mostrarRazones = pais === PAIS_VINCULO_RAZONES;

  // --- Data loading ---
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newIntermediarios, newClientes, newUsuarios, newProveedores, newRazones] = await Promise.all([
          getIntermediarios({ pais }),
          getClientes({ pais }).catch(() => []),
          getUsers().catch(() => []),
          mostrarRazones ? getProveedores(pais, false).catch(() => []) : Promise.resolve([]),
          mostrarRazones
            ? getRazonesSocialesIntermediarioFacturadas(pais).catch(() => new Set())
            : Promise.resolve(new Set()),
        ]);
        setIntermediarios(newIntermediarios || []);
        setClientes(newClientes || []);
        setUsuarios(newUsuarios || []);
        setProveedores(newProveedores || []);
        setRazonesSugeridas(newRazones || new Set());
      } catch (error) {
        log.error("CargarIntermediario fetchData", error);
        toast.error("Error al cargar datos");
      }
    };
    fetchData();
  }, [pais, mostrarRazones]);

  // --- Conteos por intermediario ---
  const clientesPorInter = useMemo(() => {
    const map = {};
    for (const c of clientes) {
      const agId = parseInt(c.agencia, 10);
      if (!isNaN(agId)) map[agId] = (map[agId] || 0) + 1;
    }
    return map;
  }, [clientes]);

  const usuariosPorInter = useMemo(() => {
    const map = {};
    for (const u of usuarios) {
      if (u.agencia) {
        const agId = parseInt(u.agencia, 10);
        if (!isNaN(agId)) map[agId] = (map[agId] || 0) + 1;
      }
    }
    return map;
  }, [usuarios]);

  // --- Razones sociales (proveedores) vinculadas por intermediario (regla 10.7: por ID) ---
  const proveedoresPorInter = useMemo(() => {
    const map = {};
    for (const p of proveedores) {
      if (p.intermediario_id != null) {
        const id = parseInt(p.intermediario_id, 10);
        if (!isNaN(id)) map[id] = (map[id] || 0) + 1;
      }
    }
    return map;
  }, [proveedores]);

  // --- Holdings raíz (sin holding_id propio) para el selector de Holding/Grupo ---
  const holdingsDisponibles = useMemo(
    () =>
      intermediarios
        .filter((i) => i.holding_id == null && (!editingItem || i.id !== editingItem.id))
        .sort((a, b) => String(a.nombre || "").localeCompare(String(b.nombre || ""), "es")),
    [intermediarios, editingItem]
  );

  // --- Comisión vigente ---
  const getComisionVigente = useCallback((inter) => {
    if (inter.comision_2 == null || !inter.comision_2_fechainicio || !inter.comision_2_fechafin) {
      return inter.comision || 0;
    }
    const hoy = new Date();
    const inicio = new Date(inter.comision_2_fechainicio);
    const fin = new Date(inter.comision_2_fechafin);
    if (hoy >= inicio && hoy <= fin) return inter.comision_2;
    return inter.comision || 0;
  }, []);

  // --- Sort (hook canónico useTableSort, regla 10.203) ---
  // Los accessors de "vigente"/"clientes"/"usuarios" dependen de un helper y de los
  // mapas de conteo (scope del componente), así que columnDefs se memoiza acá: su
  // identidad es dependencia de sortRows. Ciclo de 3 estados asc→desc→sin orden.
  const columnDefs = useMemo(
    () => ({
      nombre: { accessor: (i) => i.nombre, type: "text", nulls: "last" },
      comision: { accessor: (i) => i.comision, type: "number", nulls: "last" },
      comision_2: { accessor: (i) => i.comision_2, type: "number", nulls: "last" },
      vigente: { accessor: (i) => getComisionVigente(i), type: "number", nulls: "last" },
      clientes: { accessor: (i) => clientesPorInter[i.id] || 0, type: "number", nulls: "last" },
      usuarios: { accessor: (i) => usuariosPorInter[i.id] || 0, type: "number", nulls: "last" },
      razones: { accessor: (i) => proveedoresPorInter[i.id] || 0, type: "number", nulls: "last" },
    }),
    [getComisionVigente, clientesPorInter, usuariosPorInter, proveedoresPorInter]
  );

  const { handleSort, sortIndicator, sortRows } = useTableSort(columnDefs);

  // Filtro por nombre (buscador, sin cambios); el orden lo aplica sortRows. En "sin
  // orden" (3er click) queda el orden natural: nombre asc de getIntermediarios.
  const filteredIntermediarios = useMemo(() => {
    const searchLower = searchNombre.toLowerCase().trim();
    return intermediarios.filter((i) => !searchLower || i.nombre?.toLowerCase().includes(searchLower));
  }, [intermediarios, searchNombre]);

  const sortedIntermediarios = sortRows(filteredIntermediarios);

  // --- Modal handlers ---
  const handleOpenAdd = useCallback(() => {
    setEditingItem(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  }, []);

  const handleOpenEdit = useCallback((inter) => {
    setEditingItem(inter);
    setFormData({
      nombre: inter.nombre || "",
      comision: inter.comision ?? "",
      comision_2: inter.comision_2 ?? "",
      comision_2_fechainicio: inter.comision_2_fechainicio || "",
      comision_2_fechafin: inter.comision_2_fechafin || "",
      es_acuerdo_volumen: inter.es_acuerdo_volumen || false,
      holding_id: inter.holding_id != null ? String(inter.holding_id) : "",
    });
    setShowModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingItem(null);
    setFormData(EMPTY_FORM);
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }, []);

  // --- Save (create or update) ---
  const handleSave = useCallback(async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (formData.comision === "" || formData.comision === null) {
      toast.error("La comisión es obligatoria");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        nombre: quitarCaracteresEspeciales(formData.nombre.trim()),
        comision: parseFloat(formData.comision),
        comision_2: formData.comision_2 !== "" ? parseFloat(formData.comision_2) : null,
        comision_2_fechainicio: formData.comision_2 !== "" ? formData.comision_2_fechainicio || null : null,
        comision_2_fechafin: formData.comision_2 !== "" ? formData.comision_2_fechafin || null : null,
        es_acuerdo_volumen: !!formData.es_acuerdo_volumen,
        holding_id: formData.holding_id !== "" ? parseInt(formData.holding_id, 10) : null,
        lastupdate: new Date().toISOString(),
      };

      if (editingItem) {
        // Update
        await updateIntermediario({ id: editingItem.id, ...payload });
        setIntermediarios((prev) => prev.map((i) => (i.id === editingItem.id ? { ...i, ...payload, id: i.id } : i)));
        toast.success("Agencia actualizada correctamente");
      } else {
        // Create
        payload.pais = pais;
        const result = await saveIntermediario(payload);
        const newItem = Array.isArray(result) ? result[0] : result;
        setIntermediarios((prev) => [...prev, newItem]);
        toast.success("Agencia creada correctamente");
      }
      handleCloseModal();
    } catch (error) {
      if (error.code === "23505") {
        toast.error("Ya existe una agencia con ese nombre");
      } else {
        log.error("handleSave intermediario", error);
        toast.error("Error al guardar la agencia");
      }
    } finally {
      setSaving(false);
    }
  }, [formData, editingItem, pais, handleCloseModal]);

  // --- Delete ---
  const handleDelete = useCallback(
    async (inter) => {
      const numClientes = clientesPorInter[inter.id] || 0;
      const numUsuarios = usuariosPorInter[inter.id] || 0;

      let msg = `¿Eliminar "${inter.nombre}"?`;
      if (numClientes > 0 || numUsuarios > 0) {
        msg += `\n\nADVERTENCIA: Tiene ${numClientes} cliente(s) y ${numUsuarios} usuario(s) asociados.`;
      }

      if (!window.confirm(msg)) return;

      try {
        await deleteIntermediario(inter.id);
        setIntermediarios((prev) => prev.filter((i) => i.id !== inter.id));
        toast.success("Agencia eliminada");
      } catch (error) {
        log.error("handleDelete intermediario", error);
        toast.error("Error al eliminar la agencia");
      }
    },
    [clientesPorInter, usuariosPorInter]
  );

  // --- Detail modal (clientes/usuarios asociados) ---
  const handleOpenDetail = useCallback((type, inter) => {
    setDetailModal({ type, inter });
  }, []);

  const detailItems = useMemo(() => {
    if (!detailModal) return [];
    const { type, inter } = detailModal;
    if (type === "clientes") {
      return clientes.filter((c) => parseInt(c.agencia, 10) === inter.id);
    }
    return usuarios.filter((u) => parseInt(u.agencia, 10) === inter.id);
  }, [detailModal, clientes, usuarios]);

  // --- Vínculo razón social ↔ agencia ---
  const handleOpenVinculo = useCallback((inter) => {
    setSearchProv("");
    setVinculoModal(inter);
  }, []);

  const handleCloseVinculo = useCallback(() => {
    setVinculoModal(null);
    setSearchProv("");
  }, []);

  // Aplica un cambio de intermediario_id a un proveedor y refleja el patch por ID (regla 10.234:
  // patch puntual, no refetch del dataset). null = desvincular.
  const setVinculoProveedor = useCallback(async (prov, intermediarioId) => {
    setVinculando(true);
    try {
      await actualizarProveedor(prov.id, { intermediario_id: intermediarioId });
      setProveedores((prev) => prev.map((p) => (p.id === prov.id ? { ...p, intermediario_id: intermediarioId } : p)));
      toast.success(intermediarioId == null ? "Razón social desvinculada" : "Razón social vinculada a la agencia");
    } catch (error) {
      log.error("setVinculoProveedor", error);
      toast.error("Error al actualizar el vínculo");
    } finally {
      setVinculando(false);
    }
  }, []);

  // Proveedores vinculados a la agencia abierta en el modal.
  const proveedoresVinculados = useMemo(() => {
    if (!vinculoModal) return [];
    return proveedores
      .filter((p) => parseInt(p.intermediario_id, 10) === vinculoModal.id)
      .sort((a, b) => getNombreProveedor(a).localeCompare(getNombreProveedor(b), "es"));
  }, [vinculoModal, proveedores]);

  // Resultados para AGREGAR: sin buscar, muestra los SUGERIDOS aún no vinculados a esta agencia
  // (nombre coincide con facturacion.intermediariorazonsocial); con texto, filtra por nombre.
  // Nunca auto-vincula (regla 10.4): el usuario confirma cada fila.
  const resultadosBusqueda = useMemo(() => {
    if (!vinculoModal) return [];
    const term = searchProv.trim().toLowerCase();
    const esSugerido = (p) =>
      razonesSugeridas.has(normalizarNombreSugerencia(p.nombre)) ||
      razonesSugeridas.has(normalizarNombreSugerencia(p.razon_social));
    const disponibles = proveedores.filter((p) => parseInt(p.intermediario_id, 10) !== vinculoModal.id);
    const coincideTexto = (p) =>
      getNombreProveedor(p).toLowerCase().includes(term) || (p.razon_social || "").toLowerCase().includes(term);
    const base = term ? disponibles.filter(coincideTexto) : disponibles.filter(esSugerido);
    return base
      .map((p) => ({ prov: p, sugerido: esSugerido(p) }))
      .sort((a, b) => {
        if (a.sugerido !== b.sugerido) return a.sugerido ? -1 : 1;
        return getNombreProveedor(a.prov).localeCompare(getNombreProveedor(b.prov), "es");
      })
      .slice(0, 50);
  }, [vinculoModal, searchProv, proveedores, razonesSugeridas]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const filtersContent = useMemo(
    () => (
      <div className={styles.filtersContainer}>
        <input
          type="text"
          placeholder="Buscar nombre..."
          value={searchNombre}
          onChange={(e) => setSearchNombre(e.target.value)}
        />
        {esAdmin && (
          <button type="button" className="btn btn-primary" onClick={handleOpenAdd}>
            + Agregar Agencia
          </button>
        )}
      </div>
    ),
    [searchNombre, esAdmin, handleOpenAdd]
  );

  return (
    <div className={styles.containerIntermediarios}>
      <Header expandChildren>{filtersContent}</Header>

      <div className={styles.intermediariosTable}>
        <table>
          <thead>
            <tr>
              <th className={styles.colNombre} onClick={() => handleSort("nombre")}>
                Nombre{sortIndicator("nombre")}
              </th>
              <th className={styles.colComision} onClick={() => handleSort("comision")}>
                Comisión{sortIndicator("comision")}
              </th>
              <th className={styles.colComision2} onClick={() => handleSort("comision_2")}>
                Comisión 2{sortIndicator("comision_2")}
              </th>
              <th className={styles.colVigente} onClick={() => handleSort("vigente")}>
                Vigente{sortIndicator("vigente")}
              </th>
              <th className={styles.colClientes} onClick={() => handleSort("clientes")}>
                Clientes{sortIndicator("clientes")}
              </th>
              <th className={styles.colUsuarios} onClick={() => handleSort("usuarios")}>
                Usuarios{sortIndicator("usuarios")}
              </th>
              {mostrarRazones && (
                <th className={styles.colUsuarios} onClick={() => handleSort("razones")}>
                  Razones sociales{sortIndicator("razones")}
                </th>
              )}
              {esAdmin && <th className={styles.colAcciones}>Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {sortedIntermediarios.map((inter) => {
              const numClientes = clientesPorInter[inter.id] || 0;
              const numUsuarios = usuariosPorInter[inter.id] || 0;
              const numRazones = proveedoresPorInter[inter.id] || 0;
              return (
                <tr key={inter.id}>
                  <td className={styles.colNombre}>{inter.nombre}</td>
                  <td className={styles.colComision}>{inter.comision}%</td>
                  <td className={styles.colComision2}>{inter.comision_2 != null ? `${inter.comision_2}%` : "-"}</td>
                  <td className={styles.colVigente}>
                    <strong>{getComisionVigente(inter)}%</strong>
                  </td>
                  <td className={styles.colClientes}>
                    <span
                      className={`${styles.badge} ${numClientes > 0 ? styles.active : ""} ${numClientes > 0 ? styles.clickable : ""}`}
                      onClick={() => numClientes > 0 && handleOpenDetail("clientes", inter)}
                    >
                      {numClientes}
                    </span>
                  </td>
                  <td className={styles.colUsuarios}>
                    <span
                      className={`${styles.badge} ${numUsuarios > 0 ? styles.active : ""} ${numUsuarios > 0 ? styles.clickable : ""}`}
                      onClick={() => numUsuarios > 0 && handleOpenDetail("usuarios", inter)}
                    >
                      {numUsuarios}
                    </span>
                  </td>
                  {mostrarRazones && (
                    <td className={styles.colUsuarios}>
                      <span
                        className={`${styles.badge} ${numRazones > 0 ? styles.active : ""} ${styles.clickable}`}
                        onClick={() => handleOpenVinculo(inter)}
                        title="Gestionar razones sociales vinculadas"
                      >
                        {numRazones}
                      </span>
                    </td>
                  )}
                  {esAdmin && (
                    <td className={styles.colAcciones}>
                      <button className={styles.actionBtn} onClick={() => handleOpenEdit(inter)} title="Editar">
                        <FaEdit />
                      </button>
                      <button
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        onClick={() => handleDelete(inter)}
                        title="Eliminar"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {sortedIntermediarios.length === 0 && (
              <tr>
                <td
                  colSpan={6 + (mostrarRazones ? 1 : 0) + (esAdmin ? 1 : 0)}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No se encontraron agencias
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className={styles.modalOverlay} onClick={handleCloseModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{editingItem ? "Editar Agencia" : "Agregar Agencia"}</h3>
              <button onClick={handleCloseModal}>&times;</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label>Nombre</label>
                <input
                  className="form-control"
                  name="nombre"
                  type="text"
                  placeholder="Nombre de la agencia"
                  value={formData.nombre}
                  onChange={handleInputChange}
                />
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Comisión %</label>
                  <input
                    className="form-control"
                    name="comision"
                    type="number"
                    step="0.1"
                    placeholder="10"
                    value={formData.comision}
                    onChange={handleInputChange}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Comisión 2 % (opcional)</label>
                  <input
                    className="form-control"
                    name="comision_2"
                    type="number"
                    step="0.1"
                    placeholder="-"
                    value={formData.comision_2}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              {formData.comision_2 !== "" && formData.comision_2 !== null && (
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Fecha inicio comisión 2</label>
                    <input
                      className="form-control"
                      name="comision_2_fechainicio"
                      type="date"
                      value={formData.comision_2_fechainicio}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Fecha fin comisión 2</label>
                    <input
                      className="form-control"
                      name="comision_2_fechafin"
                      type="date"
                      value={formData.comision_2_fechafin}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}
              <div className={styles.formGroup}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    name="es_acuerdo_volumen"
                    checked={!!formData.es_acuerdo_volumen}
                    onChange={handleInputChange}
                  />
                  Acuerdo de volumen
                </label>
              </div>
              <div className={styles.formGroup}>
                <label>Holding / Grupo</label>
                <select
                  className="form-control"
                  name="holding_id"
                  value={formData.holding_id}
                  onChange={handleInputChange}
                >
                  <option value="">Sin holding</option>
                  {holdingsDisponibles.map((h) => (
                    <option key={h.id} value={String(h.id)}>
                      {h.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className="btn btn-secondary" onClick={handleCloseModal}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailModal && (
        <div className={styles.modalOverlay} onClick={() => setDetailModal(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                {detailModal.type === "clientes" ? "Clientes" : "Usuarios"} — {detailModal.inter.nombre}
              </h3>
              <button onClick={() => setDetailModal(null)}>&times;</button>
            </div>
            <div className={styles.modalBody}>
              {detailItems.length === 0 ? (
                <p style={{ textAlign: "center", color: "#6c757d" }}>Sin registros</p>
              ) : detailModal.type === "clientes" ? (
                <table className={styles.detailTable}>
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Empresa</th>
                      <th>Último contacto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailItems.map((c) => (
                      <tr key={c.id}>
                        <td>{c.nombre}</td>
                        <td>{c.empresa !== c.producto ? c.empresa : "-"}</td>
                        <td>{formatDate(c.ultimo_contacto_fecha)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className={styles.detailTable}>
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th>Cargo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailItems.map((u) => (
                      <tr key={u.id}>
                        <td>{`${u.nombre} ${u.apellido || ""}`.trim()}</td>
                        <td>{u.email || "-"}</td>
                        <td>{u.cargo || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className="btn btn-secondary" onClick={() => setDetailModal(null)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {vinculoModal && (
        <div className={styles.modalOverlay} onClick={handleCloseVinculo}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Razones sociales — {vinculoModal.nombre}</h3>
              <button onClick={handleCloseVinculo}>&times;</button>
            </div>
            <div className={styles.modalBody}>
              <h4 style={{ marginTop: 0 }}>Vinculadas ({proveedoresVinculados.length})</h4>
              {proveedoresVinculados.length === 0 ? (
                <p style={{ color: "#6c757d" }}>Sin razones sociales vinculadas.</p>
              ) : (
                <table className={styles.detailTable}>
                  <tbody>
                    {proveedoresVinculados.map((p) => (
                      <tr key={p.id}>
                        <td>
                          {getNombreProveedor(p)}
                          {p.rut_ruc ? <span style={{ color: "#6c757d" }}> — {p.rut_ruc}</span> : null}
                        </td>
                        <td style={{ textAlign: "right", width: "1%", whiteSpace: "nowrap" }}>
                          {esAdmin && (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setVinculoProveedor(p, null)}
                              disabled={vinculando}
                            >
                              Quitar
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {esAdmin && (
                <>
                  <h4 style={{ marginTop: "16px" }}>Agregar razón social</h4>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Buscar proveedor por nombre o RUT..."
                    value={searchProv}
                    onChange={(e) => setSearchProv(e.target.value)}
                    style={{ marginBottom: "8px" }}
                  />
                  {resultadosBusqueda.length === 0 ? (
                    <p style={{ color: "#6c757d" }}>
                      {searchProv.trim()
                        ? "Sin resultados."
                        : "Buscá un proveedor por nombre. Los sugeridos (coinciden con la facturación histórica) aparecen acá; la confirmación es siempre manual."}
                    </p>
                  ) : (
                    <table className={styles.detailTable}>
                      <tbody>
                        {resultadosBusqueda.map(({ prov, sugerido }) => {
                          const yaOtraAgencia = prov.intermediario_id != null;
                          return (
                            <tr key={prov.id}>
                              <td>
                                {getNombreProveedor(prov)}
                                {prov.rut_ruc ? <span style={{ color: "#6c757d" }}> — {prov.rut_ruc}</span> : null}
                                {sugerido && (
                                  <span
                                    style={{
                                      marginLeft: "6px",
                                      fontSize: "11px",
                                      padding: "1px 6px",
                                      borderRadius: "4px",
                                      background: "#d1e7dd",
                                      color: "#0f5132",
                                    }}
                                  >
                                    sugerido
                                  </span>
                                )}
                                {yaOtraAgencia && (
                                  <span style={{ marginLeft: "6px", fontSize: "11px", color: "#b02a37" }}>
                                    (vinculada a otra agencia)
                                  </span>
                                )}
                              </td>
                              <td style={{ textAlign: "right", width: "1%", whiteSpace: "nowrap" }}>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => setVinculoProveedor(prov, vinculoModal.id)}
                                  disabled={vinculando}
                                >
                                  {yaOtraAgencia ? "Mover aquí" : "Vincular"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button className="btn btn-secondary" onClick={handleCloseVinculo}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
